import React, { useEffect, useRef, useState } from 'react';
import { X, Upload, Trash2, Loader } from 'lucide-react';
import { createImportBatch, uploadImportFiles, startImport, cancelImport } from '../../services/expenseService';
import { MAX_ITEMS } from '../../utils/expenseOptions';
import { acceptFiles, chunkFiles, formatSize } from '../../utils/receiptRows';
import { ghost, muted, rowBorder, errorText, plural } from '../../pages/time/styles';

const FILES_PER_REQUEST = 8;
const BYTES_PER_REQUEST = 30 * 1024 * 1024; // under the 50 MB request limit with room for the form framing

/**
 * Choose many receipts and hand them to the server. They are read there, one by one, after you
 * leave: the lines appear on the report, and an email says when. Files are sent in small requests;
 * a failed request keeps the rest so it can simply be tried again.
 */
const ReceiptImportDialog = ({ report, onClose, onStarted }) => {
    const [files, setFiles] = useState([]);
    const [rejected, setRejected] = useState([]);
    const [phase, setPhase] = useState('choose'); // choose | uploading | error | started
    const [sent, setSent] = useState(0);
    const [error, setError] = useState('');
    const [dropFocused, setDropFocused] = useState(false);

    const batchId = useRef(null);
    const dialog = useRef(null);
    const opener = useRef(null);
    const busy = useRef(false);

    useEffect(() => {
        opener.current = document.activeElement;
        dialog.current?.focus();
        return () => opener.current?.focus?.(); // back to the button that opened the window
    }, []);

    const existing = report.items?.length || 0;
    const room = Math.max(0, MAX_ITEMS - existing);
    const totalBytes = files.reduce((sum, f) => sum + f.size, 0);

    const addFiles = (list) => {
        const { accepted, rejected: skipped } = acceptFiles([...list], Math.max(0, room - files.length), files);
        setRejected(skipped);
        setFiles(prev => [...prev, ...accepted]);
    };

    const start = async () => {
        if (busy.current || !files.length) return;
        busy.current = true;
        setPhase('uploading');
        setError('');
        try {
            if (!batchId.current) batchId.current = (await createImportBatch(report.id)).id;
            let remaining = files;
            for (const group of chunkFiles(files, FILES_PER_REQUEST, BYTES_PER_REQUEST)) {
                await uploadImportFiles(report.id, batchId.current, group);
                remaining = remaining.slice(group.length);
                setFiles(remaining); // what is left to send, so a retry never sends a file twice
                setSent(n => n + group.length);
            }
            await startImport(report.id, batchId.current);
            setPhase('started');
            onStarted?.();
        } catch (e) {
            setError(errorText(e, 'The upload did not finish. Check your connection and try again; files already sent are not sent again.'));
            setPhase('error');
        } finally {
            busy.current = false;
        }
    };

    // Leaving before it started throws away what was chosen (and what was already sent), so ask first.
    const close = async () => {
        if (busy.current) return;
        if (phase !== 'started') {
            if ((files.length || sent) && !window.confirm('Leave without importing these receipts?')) return;
            if (batchId.current) { try { await cancelImport(report.id, batchId.current); } catch { /* it is cleaned up later anyway */ } }
        }
        onClose();
    };

    const trapTab = (e) => {
        if (e.key !== 'Tab' || !dialog.current) return;
        const focusable = [...dialog.current.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]')];
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    const uploading = phase === 'uploading';
    const totalToSend = sent + files.length;

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 }}>
            <div
                ref={dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="receipt-import-title"
                tabIndex={-1}
                onKeyDown={(e) => { if (e.key === 'Escape') close(); else trapTab(e); }}
                style={{ backgroundColor: 'var(--surface)', borderRadius: '1rem', width: '100%', maxWidth: 720, maxHeight: '92vh', overflow: 'auto', padding: '1.5rem', outline: 'none' }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2 id="receipt-import-title" style={{ margin: 0 }}>Bulk import receipts</h2>
                    <button type="button" aria-label="Close" onClick={close} disabled={uploading} style={{ ...ghost, padding: 6 }}><X size={18} /></button>
                </div>

                {phase === 'started' ? (
                    <div data-testid="import-started" style={{ padding: '1rem 0' }}>
                        <p style={{ fontWeight: 600 }}>Your {plural(sent, 'receipt')} {sent === 1 ? 'is' : 'are'} being read.</p>
                        <p>You can leave this page or close the browser. We will email you when they are ready, and the lines will be on this report: the ones that were read completely, and the ones that need you to fill something in.</p>
                        <button type="button" className="btn btn-primary" onClick={onClose}>Done</button>
                    </div>
                ) : (
                    <>
                        <p style={muted}>Choose all your receipts (photos or PDFs, 5 MB each, up to {room} on this report). They are read on our server after you press <strong>Start import</strong>, so you do not have to wait: we email you when they are ready.</p>

                        <label
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => { e.preventDefault(); if (!uploading) addFiles(e.dataTransfer.files); }}
                            style={{ display: 'block', position: 'relative', border: '2px dashed var(--border)', borderRadius: 'var(--radius)', padding: '1.25rem', textAlign: 'center', cursor: 'pointer', background: 'var(--card-bg)', margin: '1rem 0', outline: dropFocused ? '2px solid var(--primary, #3b82f6)' : 'none', outlineOffset: 2 }}
                        >
                            <Upload size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                            Drop receipt photos or PDFs here, or <strong>choose files</strong>
                            <input
                                type="file"
                                multiple
                                accept="image/*,application/pdf"
                                aria-label="Receipt files"
                                disabled={uploading}
                                onFocus={() => setDropFocused(true)}
                                onBlur={() => setDropFocused(false)}
                                // Visually hidden but still reachable with the keyboard (display: none is not).
                                style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden' }}
                                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                            />
                        </label>

                        {rejected.length > 0 && (
                            <ul role="alert" style={{ color: '#b45309', fontSize: 13, margin: '0 0 1rem', paddingLeft: 18 }}>
                                {rejected.map((f, i) => <li key={i}>{f.name}: {f.reason}</li>)}
                            </ul>
                        )}

                        {files.length > 0 && (
                            <ul data-testid="chosen-files" style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
                                {files.map((f, i) => (
                                    <li key={`${f.name}|${f.size}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: rowBorder }}>
                                        <span>{f.name} <span style={muted}>{formatSize(f.size)}</span></span>
                                        <button type="button" aria-label={`Remove ${f.name}`} disabled={uploading} style={{ ...ghost, padding: 4 }} onClick={() => setFiles(prev => prev.filter((_x, j) => j !== i))}><Trash2 size={14} /></button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {uploading && (
                            <p role="status" style={{ ...muted, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <Loader size={14} className="spin" /> Uploading {sent} of {totalToSend} receipts. Keep this window open until it finishes.
                            </p>
                        )}
                        {phase === 'error' && <p role="alert" style={{ color: '#dc2626', fontSize: 13 }}>{error}</p>}

                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                            <button type="button" className="btn btn-primary" onClick={start} disabled={uploading || !files.length}>
                                {phase === 'error' ? `Try again (${plural(files.length, 'receipt')})` : `Start import${files.length ? ` (${plural(files.length, 'receipt')}, ${formatSize(totalBytes)})` : ''}`}
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={close} disabled={uploading}>Cancel</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ReceiptImportDialog;
